import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { EPISODES } from "@/components/Derive";

// Épisodes disposant d'un teaser vidéo statique (/derive/teasers/{slug}-{lang}.mp4)
export const TEASER_SLUGS = [
  "prismes-foi-doute", "architecture-silence", "grand-sommeil-esprit", "exercice-souverainete",
  "salaire-exister", "grand-oubli-vivant", "art-immobilite", "icone-iconoclaste",
  "transmettre-tempete", "tyrannie-du-conteneur", "piege-de-la-facilite",
];

const L = {
  fr: { title: "Partage un teaser", preparing: "Préparation du teaser…", err: "Oups, réessaie dans un instant." },
  en: { title: "Share a teaser", preparing: "Preparing the teaser…", err: "Oops, try again in a moment." },
  es: { title: "Comparte un teaser", preparing: "Preparando el teaser…", err: "Vaya, inténtalo de nuevo en un momento." },
  nl: { title: "Deel een teaser", preparing: "Teaser voorbereiden…", err: "Oeps, probeer het zo opnieuw." },
};

const BY_SLUG = Object.fromEntries(EPISODES.map((e) => [e.slug, e]));

export const TeaserShare = () => {
  const { i18n } = useTranslation();
  const lang = ["fr", "en", "es", "nl"].includes((i18n.language || "fr").slice(0, 2)) ? (i18n.language || "fr").slice(0, 2) : "fr";
  const T = L[lang];
  const [busy, setBusy] = useState(null);

  const share = async (ep) => {
    if (busy) return;
    setBusy(ep.slug);
    const tid = toast.loading(T.preparing);
    const title = ep.title[lang] || ep.title.fr;
    try {
      const res = await fetch(`/derive/teasers/${ep.slug}-${lang}.mp4`, { cache: "no-store" });
      if (!res.ok) throw new Error("teaser");
      const blob = await res.blob();
      const file = new File([blob], `radio-cocoon-${ep.slug}-${lang}.mp4`, { type: "video/mp4" });
      toast.dismiss(tid);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], title: `${title} — Radio Cocoon`, text: "cocoonmind.com/radio" }); } catch { /* cancelled */ }
        return;
      }
      const o = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = o; a.download = file.name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(o);
    } catch {
      toast.error(T.err, { id: tid });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-12 pt-6 border-t border-white/10 flex flex-col items-center gap-3" data-testid="radio-teaser-share">
      <p className="text-foreground/50 text-xs uppercase tracking-[0.2em]">{T.title}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full max-w-xl">
        {TEASER_SLUGS.filter((s) => BY_SLUG[s]).map((s) => {
          const ep = BY_SLUG[s];
          return (
            <button key={s} onClick={() => share(ep)} disabled={busy === s} data-testid={`share-teaser-${s}`}
              className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-full border border-primary/30 bg-white/[0.03] px-4 text-xs text-foreground/85 hover:bg-primary/15 hover:border-primary/50 hover:text-primary transition-colors disabled:opacity-50">
              {busy === s ? <Loader2 size={13} className="animate-spin flex-shrink-0" /> : <Share2 size={13} className="flex-shrink-0" />}
              <span className="truncate">{ep.title[lang] || ep.title.fr}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
